import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { DataRecord } from '../../../types';
import type { SandboxTheme } from './SandboxResumen';
import { useIsMobile } from '../mobile/MobileChrome';
import { TemasMobile, type MobileMention } from '../mobile/TemasMobile';
import {
  CHAN_SHORT,
  aspectLabel,
  channelOf,
  cleanReviewText,
  fmtDay,
  fmtInt,
  isPaid,
  pct,
  str,
  toneOf,
  topicRows,
  truthy,
  type BandejaFocus,
  type InsightSeg,
  type Tone,
  type ToneCount,
  type TopicRow,
} from './resumenData';
import './sandboxTemas.css';

/**
 * Vista de Temas: qué molesta y qué gusta, y las menciones de cada tema para revisarlas una a una.
 * Usa los mismos datos, el mismo período y el mismo criterio de tono que el Resumen.
 * Con menos de 5 menciones un tema no es concluyente.
 */

type Source = 'all' | 'google' | 'meta';
type ToneFilter = 'all' | 'neg' | 'pos' | 'neu';
type Trend = { text: string; tone: 'good' | 'bad' | 'flat' };

const TAG_CLASS: Record<Trend['tone'], string> = { good: 'good', bad: 'bad', flat: 'stable' };
const TONE_LABEL: Record<Tone, string> = { pos: 'Positiva', neg: 'Negativa', neu: 'Neutra', none: 'Sin clasificar' };
const TONE_TAG: Record<Tone, string> = { pos: 'good', neg: 'bad', neu: 'stable', none: 'stable' };
const TONE_RANK: Record<Tone, number> = { neg: 0, neu: 1, pos: 2, none: 3 };

const sourceOf = (c: DataRecord): 'google' | 'meta' => (channelOf(c) === 'gbp' ? 'google' : 'meta');

function topicKeyOf(c: DataRecord): string | null {
  const a = str(c.aspecto).trim().toLowerCase();
  return !a || a === 'otro' || a === 'null' ? null : a;
}

function originLabel(c: DataRecord): string {
  return str(c.origin).toLowerCase() === 'paid' ? 'Anuncio' : 'Orgánico';
}

/** Dónde ocurrió la mención: el local en Google, o la red y el origen en Meta. */
function placeLabel(c: DataRecord): string {
  const chan = channelOf(c);
  if (chan === 'gbp') return str(c.post_title).trim() || str(c.page_id).trim() || 'Google';
  return `${chan ? CHAN_SHORT[chan] : 'Meta'} · ${originLabel(c)}`;
}

function shortText(s: string, max = 90): string {
  const t = s.trim();
  return t.length > max ? `${t.slice(0, max - 3).trimEnd()}...` : t;
}

function emptyRow(key: string, hasPrev: boolean): TopicRow {
  return {
    key,
    name: aspectLabel(key),
    total: 0,
    tone: { pos: 0, neu: 0, neg: 0, none: 0, total: 0 },
    net: null,
    negNow: 0,
    negPrev: hasPrev ? 0 : null,
    posPrev: hasPrev ? 0 : null,
  };
}

/** Cambio en menciones positivas del tema contra el período anterior. Más positivas es bueno. */
export function posTrend(r: TopicRow): Trend {
  if (r.posPrev === null || r.posPrev === undefined) return { text: 'Sin período anterior', tone: 'flat' };
  const diff = r.tone.pos - r.posPrev;
  if (diff === 0) return { text: `Sin cambio (${r.tone.pos})`, tone: 'flat' };
  const abs = Math.abs(diff);
  // Con pocas menciones un porcentaje engaña: se muestra la cantidad
  const size = r.posPrev >= 5 ? `${Math.round((abs / r.posPrev) * 100)}%` : `${abs} ${abs === 1 ? 'positiva' : 'positivas'}`;
  return { text: `${diff > 0 ? '▲' : '▼'} ${size} (de ${r.posPrev} a ${r.tone.pos})`, tone: diff > 0 ? 'good' : 'bad' };
}

/** Cambio en quejas del tema contra el período anterior. Menos quejas es bueno. */
export function topicTrend(r: TopicRow): Trend {
  if (r.negPrev === null) return { text: 'Sin período anterior', tone: 'flat' };
  if (r.negNow === 0 && r.negPrev === 0) return { text: 'Sin quejas', tone: 'flat' };
  const diff = r.negNow - r.negPrev;
  if (diff === 0) return { text: `Sin cambio (${r.negNow})`, tone: 'flat' };
  const abs = Math.abs(diff);
  // Con pocas quejas un porcentaje engaña: se muestra la cantidad
  const size = r.negPrev >= 5 ? `${Math.round((abs / r.negPrev) * 100)}%` : `${abs} ${abs === 1 ? 'queja' : 'quejas'}`;
  return { text: `${diff > 0 ? '▲' : '▼'} ${size} (de ${r.negPrev} a ${r.negNow})`, tone: diff > 0 ? 'bad' : 'good' };
}

function ToneBar({ tone }: { tone: ToneCount }) {
  const classified = tone.pos + tone.neu + tone.neg;
  const w = (n: number) => `${classified > 0 ? ((n / classified) * 100).toFixed(1) : 0}%`;
  return (
    <div
      className='sbx-tonebar'
      style={{ height: 10 }}
      role='img'
      aria-label={`${tone.pos} positivas, ${tone.neu} neutras, ${tone.neg} negativas`}
    >
      <i className='pos' style={{ width: w(tone.pos) }} />
      <i className='neu' style={{ width: w(tone.neu) }} />
      <i className='neg' style={{ width: w(tone.neg) }} />
    </div>
  );
}

function Sect({
  id,
  tag,
  title,
  subtitle,
  children,
}: {
  id?: string;
  tag: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className='sbx-section'>
      <div className='sbx-section-head'>
        <span className='sbx-tag'>{tag}</span>
        <h2>{title}</h2>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

/** Dos listas cortas: lo que más molesta y lo que más gusta. Se usa en el Resumen y en la vista Temas. */
export function TopicHighlights({ rows, onSelect }: { rows: TopicRow[]; onSelect?: (key: string) => void }) {
  const molesta = useMemo(
    () =>
      rows
        .filter((r) => r.negNow > 0)
        .sort((a, b) => b.negNow - a.negNow || b.total - a.total)
        .slice(0, 3),
    [rows],
  );
  const gusta = useMemo(
    () =>
      rows
        .filter((r) => r.tone.pos > 0)
        .sort((a, b) => b.tone.pos - a.tone.pos || a.negNow - b.negNow)
        .slice(0, 3),
    [rows],
  );

  const list = (items: TopicRow[], kind: 'neg' | 'pos') =>
    items.length === 0 ? (
      <div className='sbx-empty'>Sin casos en este período.</div>
    ) : (
      items.map((r) => {
        const tr = kind === 'neg' ? topicTrend(r) : posTrend(r);
        const n = kind === 'neg' ? r.negNow : r.tone.pos;
        return (
          <button key={r.key} type='button' className='sbx-th-row' onClick={() => onSelect?.(r.key)}>
            <strong>{r.name}</strong>
            <span className='n'>
              {fmtInt(n)} {kind === 'neg' ? (n === 1 ? 'queja' : 'quejas') : n === 1 ? 'positiva' : 'positivas'}
            </span>
            <span className={`t sbx-loc-tag ${TAG_CLASS[tr.tone]}`}>{tr.text}</span>
          </button>
        );
      })
    );

  return (
    <div className='sbx-th'>
      <section className='sbx-card sbx-th-col' aria-label='Lo que más molesta'>
        <h3>Lo que más molesta</h3>
        <p>Quejas del período contra el período anterior</p>
        {list(molesta, 'neg')}
      </section>
      <section className='sbx-card sbx-th-col' aria-label='Lo que más gusta'>
        <h3>Lo que más gusta</h3>
        <p>Menciones positivas del período contra el período anterior</p>
        {list(gusta, 'pos')}
      </section>
    </div>
  );
}

interface Props {
  theme: SandboxTheme;
  /** Comentarios del período activo, sin respuestas propias. */
  rangeComments: DataRecord[];
  /** Período anterior de igual largo. null si no hay. */
  prevComments: DataRecord[] | null;
  periodLabel: string;
  prevLabel: string;
  urgentCount: number;
  showHeader?: boolean;
  /** Tema que se abre de entrada, por ejemplo al llegar desde el Resumen. */
  initialTopic?: string | null;
  onOpenBandeja: (focus?: BandejaFocus) => void;
  onNavigateToResumen: () => void;
  onNavigateSection?: (sectionId: string) => void;
  onNavigateToLocales?: () => void;
  onExportPdf?: () => void;
}

export function SandboxTemas({
  theme,
  rangeComments,
  prevComments,
  periodLabel,
  prevLabel,
  urgentCount,
  showHeader = true,
  initialTopic = null,
  onOpenBandeja,
  onNavigateToResumen,
  onNavigateSection,
  onNavigateToLocales,
  onExportPdf,
}: Props) {
  const rootRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const [source, setSource] = useState<Source>('all');
  const [topic, setTopic] = useState<string | null>(initialTopic);
  const [toneFilter, setToneFilter] = useState<ToneFilter>('all');
  const [idx, setIdx] = useState(0);

  // Menciones con tema asignado por canal, para los botones del filtro
  const counts = useMemo(() => {
    const out = { all: 0, google: 0, meta: 0 };
    for (const c of rangeComments) {
      if (!topicKeyOf(c)) continue;
      out.all += 1;
      out[sourceOf(c)] += 1;
    }
    return out;
  }, [rangeComments]);
  const showSource = counts.google > 0 && counts.meta > 0;
  const activeSource: Source = showSource ? source : 'all';

  const scopedCur = useMemo(
    () => rangeComments.filter((c) => activeSource === 'all' || sourceOf(c) === activeSource),
    [rangeComments, activeSource],
  );
  const scopedPrev = useMemo(
    () => (prevComments ? prevComments.filter((c) => activeSource === 'all' || sourceOf(c) === activeSource) : null),
    [prevComments, activeSource],
  );

  const summary = useMemo(() => topicRows(scopedCur, scopedPrev, 100), [scopedCur, scopedPrev]);

  // Todos los temas del período, para que la lista no cambie de forma al cambiar de canal
  const rows = useMemo(() => {
    const merged: TopicRow[] = [...summary.rows];
    const have = new Set(merged.map((r) => r.key));
    for (const r of topicRows(rangeComments, null, 100).rows) {
      if (!have.has(r.key)) merged.push(emptyRow(r.key, scopedPrev !== null));
    }
    return merged.sort((a, b) => b.negNow - a.negNow || b.total - a.total || a.name.localeCompare(b.name));
  }, [summary, rangeComments, scopedPrev]);

  const withMentions = useMemo(() => rows.filter((r) => r.total > 0), [rows]);
  const active = rows.find((r) => r.key === topic && r.total > 0) ?? withMentions[0] ?? null;

  useEffect(() => {
    setIdx(0);
  }, [active?.key, toneFilter, activeSource]);

  const mentions = useMemo(() => {
    if (!active) return [];
    return scopedCur
      .filter((c) => topicKeyOf(c) === active.key && (toneFilter === 'all' || toneOf(c) === toneFilter))
      .sort((a, b) => TONE_RANK[toneOf(a)] - TONE_RANK[toneOf(b)] || str(b.date).localeCompare(str(a.date)));
  }, [scopedCur, active, toneFilter]);
  const pos = mentions.length > 0 ? Math.min(idx, mentions.length - 1) : 0;
  const current = mentions[pos] ?? null;

  // Dónde se concentran las quejas del tema. Si no tiene quejas, dónde se destaca.
  const focusTone: Tone = active && active.tone.neg > 0 ? 'neg' : 'pos';
  const byPlace = useMemo(() => {
    if (!active) return [] as Array<[string, number]>;
    const m = new Map<string, number>();
    for (const c of scopedCur) {
      if (topicKeyOf(c) !== active.key || toneOf(c) !== focusTone) continue;
      const k = placeLabel(c);
      m.set(k, (m.get(k) ?? 0) + 1);
    }
    return Array.from(m.entries()).sort((a, b) => b[1] - a[1]);
  }, [scopedCur, active, focusTone]);
  const maxPlace = byPlace.length > 0 ? byPlace[0][1] : 1;

  const insight = useMemo((): InsightSeg[] | null => {
    if (withMentions.length === 0) return null;
    const quejas = withMentions.reduce((a, r) => a + r.negNow, 0);
    const top = withMentions[0];
    const best = [...withMentions].sort((a, b) => b.tone.pos - a.tone.pos)[0];
    const scope = activeSource === 'google' ? ' en Google My Business' : activeSource === 'meta' ? ' en Meta' : '';
    const segs: InsightSeg[] = [];
    if (top && top.negNow > 0) {
      segs.push(
        { text: top.name, bold: true },
        { text: ` concentra ${top.negNow} de las ${quejas} ${quejas === 1 ? 'queja' : 'quejas'}${scope}. ` },
      );
    }
    if (best && best.tone.pos > 0) {
      segs.push(
        { text: 'El tema más elogiado es ' },
        { text: best.name, bold: true },
        { text: ` con ${best.tone.pos} ${best.tone.pos === 1 ? 'mención positiva' : 'menciones positivas'}.` },
      );
    }
    return segs.length > 0 ? segs : null;
  }, [withMentions, activeSource]);

  // Flechas del teclado para recorrer las menciones
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && ['SELECT', 'INPUT', 'TEXTAREA'].includes(t.tagName)) return;
      if (e.key === 'ArrowRight') setIdx((i) => Math.min(i + 1, Math.max(0, mentions.length - 1)));
      if (e.key === 'ArrowLeft') setIdx((i) => Math.max(0, i - 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mentions.length]);

  // Al llegar con un tema elegido se baja al explorador; si no, la vista parte desde arriba.
  useEffect(() => {
    if (initialTopic) {
      document.getElementById('temas-explorador')?.scrollIntoView({ block: 'start' });
    } else {
      rootRef.current?.scrollIntoView({ block: 'start' });
    }
  }, [initialTopic]);

  const selectTopic = (key: string, scroll: boolean) => {
    setTopic(key);
    setToneFilter('all');
    if (scroll) document.getElementById('temas-explorador')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const goSection = (id: string) => {
    if (onNavigateSection) onNavigateSection(id);
    else onNavigateToResumen();
  };

  const sourcePills: Array<[Source, string, number]> = [
    ['all', 'Todos', counts.all],
    ['google', 'Google My Business', counts.google],
    ['meta', 'Meta', counts.meta],
  ];
  const toneTabs: Array<[ToneFilter, string, number]> = active
    ? [
        ['all', 'Todas', active.total],
        ['neg', 'Negativas', active.tone.neg],
        ['pos', 'Positivas', active.tone.pos],
        ['neu', 'Neutras', active.tone.neu],
      ]
    : [];
  const trend = active ? topicTrend(active) : null;
  const bandejaGroup = activeSource === 'meta' ? 'meta' : 'google';

  const header = showHeader ? (
    <header className='sbx-top-bar'>
      <div className='sbx-top-bar-inner'>
        <div className='sbx-brand'>
          <span className='sbx-brand-title'>Social Wolf</span>
        </div>
        <nav className='sbx-nav' aria-label='Secciones'>
          <button type='button' className='sbx-nav-link' onClick={onNavigateToResumen}>
            Resumen
          </button>
          <button type='button' className='sbx-nav-link' onClick={() => onOpenBandeja()}>
            Bandeja {urgentCount > 0 && <span className='sbx-nav-badge'>{urgentCount}</span>}
          </button>
          <button type='button' className='sbx-nav-link' onClick={() => goSection('canales')}>
            Canales
          </button>
          <button type='button' className='sbx-nav-link active'>
            Temas
          </button>
          {onNavigateToLocales && (
            <button type='button' className='sbx-nav-link' onClick={onNavigateToLocales}>
              Locales
            </button>
          )}
          <button type='button' className='sbx-nav-link' onClick={() => goSection('plan')}>
            Plan
          </button>
        </nav>
        {onExportPdf && (
          <div className='sbx-top-right'>
            <button type='button' className='sbx-btn-pdf' onClick={onExportPdf}>
              Exportar PDF
            </button>
          </div>
        )}
      </div>
    </header>
  ) : null;

  if (isMobile) {
    const toMention = (c: DataRecord): MobileMention => {
      const chan = channelOf(c);
      const post = str(c.post_title).trim();
      return {
        id: str(c.feedback_id) || `${str(c.author_name)}|${str(c.date)}`,
        channel: chan ? CHAN_SHORT[chan] : 'Canal',
        place: chan === 'gbp' ? placeLabel(c) : originLabel(c),
        stars: Number(c.star_rating) || 0,
        date: fmtDay(str(c.date)),
        post: chan !== 'gbp' && post ? shortText(post) : null,
        text: cleanReviewText(c.message) || 'Sin texto',
        tone: toneOf(c),
        answered: truthy(c.has_reply),
        gbp: chan === 'gbp',
        paid: isPaid(c),
        feedbackId: str(c.feedback_id),
        source: sourceOf(c),
      };
    };
    return (
      <div className='sbx' data-theme={theme} ref={rootRef}>
        <TemasMobile
          theme={theme}
          pills={showSource ? sourcePills.map(([id, label, n]) => ({ id, label: `${label} ${fmtInt(n)}` })) : []}
          source={activeSource}
          onSource={(id) => setSource(id as Source)}
          topics={withMentions.map((r) => ({
            key: r.key,
            name: r.name,
            total: r.total,
            tone: r.tone,
            negNow: r.negNow,
            trend: topicTrend(r),
            low: r.total < 5,
          }))}
          initialTopic={initialTopic}
          mentionsOf={(key, tf) =>
            scopedCur
              .filter((c) => topicKeyOf(c) === key && (tf === 'all' || toneOf(c) === tf))
              .sort((a, b) => TONE_RANK[toneOf(a)] - TONE_RANK[toneOf(b)] || str(b.date).localeCompare(str(a.date)))
              .map(toMention)
          }
          placesOf={(key) => {
            const row = rows.find((r) => r.key === key);
            const focus: Tone = row && row.tone.neg > 0 ? 'neg' : 'pos';
            const m = new Map<string, number>();
            for (const c of scopedCur) {
              if (topicKeyOf(c) !== key || toneOf(c) !== focus) continue;
              const k = placeLabel(c);
              m.set(k, (m.get(k) ?? 0) + 1);
            }
            return { focus: focus === 'neg' ? 'neg' : 'pos', rows: Array.from(m.entries()).sort((a, b) => b[1] - a[1]) };
          }}
          bandejaGroup={bandejaGroup}
          onOpenBandeja={onOpenBandeja}
        />
      </div>
    );
  }

  return (
    <div className='sbx' data-theme={theme} ref={rootRef}>
      {header}

      <section className='sbx-head'>
        <div className='sbx-head-title'>
          <h1>Temas</h1>
          <p>
            {periodLabel}
            {prevLabel ? ` · comparado con ${prevLabel}` : ''}.
          </p>
        </div>
        <div className='sbx-head-controls'>
          {showSource && (
            <div className='sbx-pills' role='group' aria-label='Filtrar por canal'>
              {sourcePills.map(([id, label, n]) => (
                <button
                  key={id}
                  type='button'
                  className='sbx-pill'
                  aria-pressed={activeSource === id}
                  onClick={() => setSource(id)}
                >
                  {label} {fmtInt(n)}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {withMentions.length === 0 ? (
        <div className='sbx-empty'>No hay comentarios con tema asignado en este período.</div>
      ) : (
        <>
          {insight && (
            <section className='sbx-gert' aria-label='Lectura de Gertrudis'>
              <div className='sbx-gert-grid'>
                <div className='sbx-gert-card'>
                  <span className={`sbx-gert-label ${withMentions[0].negNow > 0 ? 'alert' : 'opportunity'}`}>Lectura</span>
                  <p>
                    {insight.map((s, i) => (s.bold ? <strong key={i}>{s.text}</strong> : <span key={i}>{s.text}</span>))}
                  </p>
                </div>
              </div>
            </section>
          )}

          <TopicHighlights rows={withMentions} onSelect={(k) => selectTopic(k, true)} />

          <Sect
            id='temas-explorador'
            tag='Temas'
            title='Revisa cada tema una a una'
            subtitle='Elige un tema para ver sus menciones. Los temas con menos de 5 menciones no son concluyentes.'
          >
            <div className='sbx-tcards' role='listbox' aria-label='Temas del período'>
              {rows.map((r) => {
                const low = r.total < 5;
                return (
                  <button
                    key={r.key}
                    type='button'
                    role='option'
                    aria-selected={active?.key === r.key}
                    disabled={r.total === 0}
                    className={`sbx-tcard${low ? ' is-low' : ''}`}
                    onClick={() => selectTopic(r.key, false)}
                  >
                    <span className='sbx-tcard-top'>
                      <strong>{r.name}</strong>
                      <span>
                        {fmtInt(r.total)} {r.total === 1 ? 'mención' : 'menciones'}
                      </span>
                    </span>
                    <ToneBar tone={r.tone} />
                    <span className='sbx-tcard-foot'>
                      <span>
                        {r.tone.neg} negativas · {r.tone.pos} positivas
                      </span>
                      {r.total === 0 ? (
                        <span className='sbx-loc-tag stable'>Sin menciones</span>
                      ) : low ? (
                        <span className='sbx-loc-tag stable'>Pocas menciones</span>
                      ) : r.negNow > 0 ? (
                        <span className='sbx-loc-tag bad'>
                          {r.negNow} {r.negNow === 1 ? 'queja' : 'quejas'}
                        </span>
                      ) : (
                        <span className='sbx-loc-tag good'>Sin quejas</span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>

            {active && trend && (
              <div className='sbx-card sbx-tdet' aria-live='polite'>
                <div className='sbx-tdet-head'>
                  <div>
                    <h3>{active.name}</h3>
                    <p>
                      {fmtInt(active.total)} {active.total === 1 ? 'mención' : 'menciones'} en el período · {active.tone.neg}{' '}
                      negativas · {active.tone.pos} positivas
                    </p>
                  </div>
                  <div className='sbx-tfilters' role='group' aria-label='Filtrar por tono'>
                    {toneTabs.map(([id, label, n]) => (
                      <button
                        key={id}
                        type='button'
                        className='sbx-pill'
                        aria-pressed={toneFilter === id}
                        onClick={() => setToneFilter(id)}
                      >
                        {label} {n}
                      </button>
                    ))}
                  </div>
                </div>

                <div className='sbx-tstats'>
                  <div>
                    <span>Menciones</span>
                    <strong>{fmtInt(active.total)}</strong>
                  </div>
                  <div>
                    <span>Quejas</span>
                    <strong>{fmtInt(active.negNow)}</strong>
                    <span className={`sbx-loc-tag ${TAG_CLASS[trend.tone]}`}>{trend.text}</span>
                  </div>
                  <div>
                    <span>Negativas</span>
                    <strong>{pct(active.tone.neg, active.total)}%</strong>
                  </div>
                </div>

                {byPlace.length > 0 && (
                  <div>
                    <h4 className='sbx-tdet-h'>
                      {focusTone === 'neg' ? 'Dónde se concentran las quejas' : 'Dónde se destaca'}
                    </h4>
                    {byPlace.map(([name, n]) => (
                      <div key={name} className='sbx-tloc'>
                        <span>{name}</span>
                        <div className='sbx-star-track'>
                          <i className={focusTone === 'neg' ? 'lo' : 'hi'} style={{ width: `${(n / maxPlace) * 100}%` }} />
                        </div>
                        <span>
                          {n} {focusTone === 'neg' ? (n === 1 ? 'queja' : 'quejas') : n === 1 ? 'positiva' : 'positivas'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {current ? (
                  <>
                    <div className='sbx-tnav'>
                      <span className='sbx-tnav-count'>
                        Mención {pos + 1} de {mentions.length}
                      </span>
                      {mentions.length <= 24 && (
                        <span className='sbx-tdots' aria-hidden='true'>
                          {mentions.map((c, i) => (
                            <i
                              key={str(c.feedback_id) || `${str(c.author_name)}|${str(c.date)}|${i}`}
                              className={`${toneOf(c)}${i === pos ? ' on' : ''}`}
                            />
                          ))}
                        </span>
                      )}
                      <span className='sbx-tnav-btns'>
                        <button
                          type='button'
                          className='sbx-btn-secondary'
                          disabled={pos === 0}
                          onClick={() => setIdx(pos - 1)}
                        >
                          Anterior
                        </button>
                        <button
                          type='button'
                          className='sbx-btn-publish'
                          disabled={pos >= mentions.length - 1}
                          onClick={() => setIdx(pos + 1)}
                        >
                          Siguiente
                        </button>
                      </span>
                    </div>

                    {(() => {
                      const t = toneOf(current);
                      const chan = channelOf(current);
                      const stars = Number(current.star_rating);
                      const answered = truthy(current.has_reply);
                      const post = str(current.post_title).trim();
                      return (
                        <article className={`sbx-men ${t}`} data-origen={isPaid(current) ? 'paid' : 'organic'}>
                          <div className='sbx-men-head'>
                            <span>
                              <strong>{chan ? CHAN_SHORT[chan] : 'Canal'}</strong> ·{' '}
                              {chan === 'gbp' ? placeLabel(current) : originLabel(current)}
                              {stars > 0 ? ` · ★ ${stars}` : ''}
                            </span>
                            <span>{fmtDay(str(current.date))}</span>
                          </div>
                          {chan !== 'gbp' && post && <p className='sbx-men-ctx'>En: «{shortText(post)}»</p>}
                          <blockquote>{cleanReviewText(current.message) || 'Sin texto'}</blockquote>
                          <div className='sbx-men-tags'>
                            <span className={`sbx-loc-tag ${TONE_TAG[t]}`}>{TONE_LABEL[t]}</span>
                            <span className='sbx-loc-tag stable'>{answered ? 'Respondida' : 'Sin responder'}</span>
                          </div>
                          {!answered && (
                            <div>
                              <button
                                type='button'
                                className='sbx-link'
                                onClick={() =>
                                  onOpenBandeja({
                                    topic: active.key,
                                    feedbackId: str(current.feedback_id),
                                    group: sourceOf(current),
                                  })
                                }
                              >
                                {chan === 'gbp' ? 'Responder en la Bandeja' : 'Ver en la Bandeja'}
                              </button>
                            </div>
                          )}
                        </article>
                      );
                    })()}
                  </>
                ) : (
                  <div className='sbx-empty'>No hay menciones con este tono.</div>
                )}

                <div>
                  <button
                    type='button'
                    className='sbx-link'
                    onClick={() => onOpenBandeja({ topic: active.key, group: bandejaGroup })}
                  >
                    Ver este tema en la Bandeja de {bandejaGroup === 'meta' ? 'Meta' : 'Google My Business'}
                  </button>
                </div>
              </div>
            )}
          </Sect>
          <p className='sbx-table-note' style={{ borderTop: 'none', padding: 0 }}>
            {fmtInt(summary.assigned)} de {fmtInt(summary.total)} comentarios tienen un tema asignado. Los nombres de los
            temas son fijos en esta versión.
          </p>
        </>
      )}
    </div>
  );
}
